import { ListItem, Paragraph, styled } from 'canoe';
import { createCacheKey, ENDPOINTS, fetchResource, GetAll, useCreatePayload } from 'hammock';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { NewListItem } from 'canoe/components/ListItem';

import { DefaultItemProps, Select, SelectProps } from '../Select';

const EnterContainer = styled(Paragraph)`
  display: flex;
  font-size: 12px;
`;

interface AutocompleteSelectProps<Item, Data, Values>
  extends Omit<SelectProps<Item, Values>, 'options'> {
  apiQuery: (search: string) => GetAll;
  getFormattedResults: (results: Data, searchValue: string) => Item[];
  onQuerySuccess?: (data: Data) => void;
  disableQueryOnEmptySearch?: boolean;
}

export const AutocompleteSelect = <Item extends DefaultItemProps, Data, Values>({
  apiQuery,
  getFormattedResults,
  onQuerySuccess,
  name,
  isMultiselect,
  disableQueryOnEmptySearch,
  ...props
}: AutocompleteSelectProps<Item, Data, Values>) => {
  const { getAll } = useCreatePayload(ENDPOINTS.autoComplete);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchParams, setSearchParams] = useState<string>('');

  const payload = useMemo(() => getAll(apiQuery(searchParams)), [apiQuery, searchParams]);

  const { data: results, isFetching: fetchingResults } = useQuery(
    createCacheKey(payload),
    () => fetchResource<Data>(payload),
    {
      enabled: disableQueryOnEmptySearch ? searchParams.trim() !== '' : true,
      onSuccess: onQuerySuccess || undefined,
    },
  );

  const items: Item[] = useMemo(() => {
    if (!results) {
      return [];
    }
    return getFormattedResults(results, searchValue);
  }, [results, apiQuery, getFormattedResults, searchValue]);

  const updateSearchParams = useCallback(
    debounce((value) => {
      setSearchParams(value);
    }, 300),
    [],
  );

  const updateSearchValue = useCallback(
    (value: string) => {
      updateSearchParams(value);
      setSearchValue(value);
    },
    [updateSearchParams],
  );

  const clearSearchValue = useCallback(() => {
    setSearchValue('');
    setSearchParams('');
  }, []);

  return (
    <Select<Item, Values>
      {...props}
      name={name}
      loading={fetchingResults}
      onSearch={updateSearchValue}
      onClearSearch={clearSearchValue}
      searchValue={searchValue}
      // @ts-ignore
      options={
        disableQueryOnEmptySearch && !items.length
          ? [
              {
                value: '',
                label: 'Start typing to see results',
                disabled: true,
              },
            ]
          : items
      }
      onValueNotFound={
        disableQueryOnEmptySearch && !items.length
          ? () => ({ showEmptyValue: false, warningText: '' })
          : props.onValueNotFound || undefined
      }
      isMultiselect={isMultiselect}
      renderItem={(itemProps: any) => {
        if (disableQueryOnEmptySearch && !items.length && !itemProps.value && itemProps.disabled) {
          return (
            <NewListItem>
              <EnterContainer color="OnyxGrayL30" mb="none">
                {itemProps.label}
              </EnterContainer>
            </NewListItem>
          );
        }

        if (props.renderItem) {
          return props.renderItem(itemProps);
        }

        return <ListItem {...itemProps}>{itemProps.label}</ListItem>;
      }}
    />
  );
};
